/**
 * Result card for one search hit.
 *
 * TODO: swap for `MomentCard` from '@/components/video' (props `{ moment, onSeek?, videoTitle?, href? }`) once it lands.
 * Search hits (`MomentSearchHit`) carry flat brand/topic strings rather than `MomentOut`, so the swap needs a small adapter.
 */
import { Link } from 'react-router-dom';
import type { MomentSearchHit } from '@/api/types';
import { Badge } from '@/components/arc/badge/badge';
import { fmtDuration, labelize, truncate } from '@/lib/format';
import styles from './ExploreHitCard.module.css';

export function ExploreHitCard({ hit, href }: { hit: MomentSearchHit; href: string }) {
  const image = hit.keyframe_url || hit.thumbnail_url || null;
  const risk = hit.risk === 'high' ? 'danger' : hit.risk === 'medium' ? 'warning' : null;
  return (
    <Link
      to={href}
      className={styles.card}
      aria-label={`${hit.video_title}, ${fmtDuration(hit.start_sec)} to ${fmtDuration(hit.end_sec)}`}
    >
      <div className={styles.media}>
        {image ? <img src={image} alt="" loading="lazy" className={styles.image} /> : null}
        <span className={`${styles.time} mono`}>
          {fmtDuration(hit.start_sec)} to {fmtDuration(hit.end_sec)}
        </span>
        {hit.score != null ? (
          <span className={`${styles.score} mono`}>{Math.round(hit.score * 100)}%</span>
        ) : null}
      </div>
      <div className={styles.body}>
        <p className={styles.meta} title={hit.video_title}>
          {hit.video_title}
          {hit.channel ? `, ${hit.channel}` : ''}
        </p>
        <p className={styles.summary}>{truncate(hit.scene_summary, 140)}</p>
        <div className={styles.tags}>
          <Badge size="sm">{labelize(hit.sentiment)}</Badge>
          {risk ? (
            <Badge size="sm" tone={risk}>
              {labelize(hit.risk)} risk
            </Badge>
          ) : null}
          {hit.brands.slice(0, 2).map((b) => (
            <Badge key={`b-${b}`} size="sm" tone="info">
              {b}
            </Badge>
          ))}
          {hit.topics.slice(0, 3).map((t) => (
            <Badge key={`t-${t}`} size="sm">
              {t}
            </Badge>
          ))}
        </div>
      </div>
    </Link>
  );
}
